import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "473",
  "slug": "what-is-a-sinkhole-how-sinkholes-form",
  "title": "What Is a Sinkhole? How Sinkholes Form and Why the Ground Suddenly Collapses",
  "excerpt": "Sinkholes can open without warning and swallow roads, cars, and houses. Here is the slow chemistry that makes them, the three main types, and why some regions are far more prone than others.",
  "date": "2026-06-18",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1719941463960-f3310fe64e46?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjZW5vdGV8ZW58MXwwfHx8MTc4MTc3MDQzNXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A sinkhole is a depression that forms when soluble rock like limestone dissolves underground, leaving a void that eventually gives way. Most form slowly over centuries, but the dramatic ones collapse in seconds when a hidden cavity loses its support. They are most common in karst regions and can be triggered or worsened by groundwater pumping and leaking pipes.",
  "content": [
    {
      "type": "paragraph",
      "text": "Few natural events feel as unsettling as a sinkhole. A patch of ordinary ground, a parking lot, a back garden, a stretch of highway, drops away into a pit, sometimes deep enough to swallow a building. What makes them so alarming is that the collapse can be sudden, even though the process that caused it has usually been underway for a very long time."
    },
    {
      "type": "heading",
      "text": "It Starts With Rock That Dissolves"
    },
    {
      "type": "paragraph",
      "text": "Most sinkholes form in landscapes underlain by soluble rock, chiefly limestone, but also gypsum, dolomite, and salt beds. Rainwater absorbs carbon dioxide from the air and soil and becomes mildly acidic. As that weak acid percolates down through cracks in the rock, it slowly dissolves the stone away, widening fractures into channels and channels into caves. Geologists call this kind of landscape karst, and it is riddled with hidden voids."
    },
    {
      "type": "paragraph",
      "text": "This is the same chemistry that carves cave systems and feeds underground rivers. A sinkhole is, in a sense, what happens when the roof of one of those slowly growing voids can no longer hold up the weight above it."
    },
    {
      "type": "heading",
      "text": "The Three Main Types of Sinkhole"
    },
    {
      "type": "paragraph",
      "text": "Not all sinkholes behave the same way. Geologists generally sort them into three families:"
    },
    {
      "type": "list",
      "items": [
        "Solution sinkholes form gradually at the surface where rock is exposed or thinly covered. The ground simply dissolves and settles into a shallow bowl over many years. These are the gentle, undramatic kind.",
        "Cover-subsidence sinkholes form where loose sediment sits over the rock. As the rock dissolves, sand and soil trickle down into the gaps, and the surface slowly sags. Again, slow and usually harmless.",
        "Cover-collapse sinkholes are the headline-makers. Here a strong layer of clay-rich soil bridges over a growing cavity, hiding it completely, until the span finally fails and the whole cover drops at once. These can open in seconds with no surface warning."
      ]
    },
    {
      "type": "paragraph",
      "text": "So the terrifying overnight sinkhole and the harmless dip in a farmer's field are two ends of the same process. The difference is mostly about what kind of material sits on top of the dissolving rock and how it gives way."
    },
    {
      "type": "heading",
      "text": "Why Humans Make Sinkholes Worse"
    },
    {
      "type": "paragraph",
      "text": "Many of the most destructive sinkholes are triggered or accelerated by people. Pumping groundwater lowers the water table, removing the buoyant support that water provided inside underground cavities and making collapse more likely. Leaking water mains and broken sewer lines wash sediment away from beneath the surface. Heavy construction loads, drainage changes, and abandoned mines all add stress. A region can sit stable for centuries and then start collapsing once development changes how water moves through the ground."
    },
    {
      "type": "heading",
      "text": "Where Sinkholes Are Most Common"
    },
    {
      "type": "paragraph",
      "text": "Sinkhole country is karst country. Florida is famously prone because it sits on porous limestone with a high, fluctuating water table. Other hotspots include parts of the Balkans, southern China, the Yucatan Peninsula, the Caribbean, and stretches of the central United States. Some of these landscapes produce spectacular features:"
    },
    {
      "type": "list",
      "items": [
        "The cenotes of Mexico's Yucatan are water-filled sinkholes that were sacred to the Maya and remain stunning swimming holes today.",
        "The Great Blue Hole off Belize is a marine sinkhole that formed on dry land during the last ice age and was later flooded by rising seas.",
        "Xiaozhai Tiankeng in China is one of the deepest sinkholes in the world, a giant pit hundreds of metres deep."
      ]
    },
    {
      "type": "heading",
      "text": "Are They Predictable?"
    },
    {
      "type": "paragraph",
      "text": "To a degree, yes. Geologists can map which areas sit on soluble rock and a high water table, and ground-surveying tools like radar and resistivity scanning can sometimes reveal voids before they collapse. Subtle warning signs occasionally appear at the surface too: doors and windows that suddenly stick as the ground shifts, fresh cracks in walls or pavement, small circular depressions, or a patch of ground that slumps after heavy rain. But cover-collapse sinkholes are notoriously hard to predict precisely, which is exactly why they make the news. The cavity does its growing in secret, and the surface gives way only at the very end."
    },
    {
      "type": "heading",
      "text": "Reading Karst From the Sky"
    },
    {
      "type": "paragraph",
      "text": "From above, karst landscapes have a distinctive pockmarked look: clusters of rounded depressions, disappearing streams, and lakes that sit in suspiciously circular basins. Once you know the signature, you start seeing it everywhere from southern China's stone forests to the lake-dotted plateaus of the Balkans. Training your eye to recognise terrain like this is part of what makes geography click, and it is exactly the skill EarthGuessr rewards: study the satellite view, read the landforms, and work out where on Earth you have landed."
    }
  ]
};
